import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  InputAdornment,
  Button,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { AccountBalanceWallet, Search, Refresh, Add, PendingActions, Close, Paid } from '@mui/icons-material';
import MainText from '../components/MainText';
import CustomModal from '../components/CustomModal';
import CustomSnackBar from '../components/CustomSnackBar';
import API, { SettlementsAPI } from '../services/api';
import { useDispatch } from 'react-redux';
import { addTab } from '../tabs/tabsSlice';

const formatCurrency = (value) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return 'Q 0.00';
  try {
    return new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ' }).format(num);
  } catch {
    return `Q ${num.toFixed(2)}`;
  }
};

const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

function DebtCreateForm({ onSave, suppliers = [] }) {
  const [form, setForm] = useState({
    supplier: null,
    date: new Date().toISOString().slice(0, 10),
    number: '',
    total_amount: '',
    description: '',
  });
  const [error, setError] = useState('');

  const handleChange = (name, value) => setForm((p) => ({ ...p, [name]: value }));

  const handleSubmit = () => {
    const supplierId = form.supplier?.id;
    const total = Number(form.total_amount || 0);
    if (!supplierId) {
      setError('Selecciona un proveedor.');
      return;
    }
    if (!Number.isFinite(total) || total <= 0) {
      setError('El monto total debe ser mayor a 0.');
      return;
    }
    setError('');
    onSave({
      supplier_id: supplierId,
      date: form.date,
      number: form.number || '',
      description: form.description || '',
      total_amount: total,
    });
  };

  return (
    <Stack spacing={2} sx={{ p: 1 }}>
      <Autocomplete
        options={suppliers}
        value={form.supplier}
        onChange={(_, v) => handleChange('supplier', v)}
        getOptionLabel={(opt) => opt?.companyName || opt?.legalName || ''}
        renderInput={(params) => <TextField {...params} label="Proveedor" size="small" />}
        isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
      />
      <TextField
        size="small"
        label="Fecha"
        type="date"
        value={form.date}
        onChange={(e) => handleChange('date', e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        size="small"
        label="Numero (opcional)"
        value={form.number}
        onChange={(e) => handleChange('number', e.target.value)}
      />
      <TextField
        size="small"
        label="Monto total"
        type="number"
        inputProps={{ step: '0.01' }}
        value={form.total_amount}
        onChange={(e) => handleChange('total_amount', e.target.value)}
      />
      <TextField
        size="small"
        label="Descripcion"
        value={form.description}
        onChange={(e) => handleChange('description', e.target.value)}
        multiline
        rows={2}
      />
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
      <Box sx={{ textAlign: 'right' }}>
        <Button variant="contained" onClick={handleSubmit}>
          Guardar
        </Button>
      </Box>
    </Stack>
  );
}

function DebtPaymentForm({ onSave, debt }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    amount: '',
    method: '',
    comment: '',
  });
  const [error, setError] = useState('');

  const saldo = Number(debt?.saldo_amount || 0);

  useEffect(() => {
    setForm({
      date: new Date().toISOString().slice(0, 10),
      amount: '',
      method: '',
      comment: '',
    });
    setError('');
  }, [debt]);

  const handleChange = (name, value) => setForm((p) => ({ ...p, [name]: value }));

  const handleSubmit = () => {
    const amount = Number(form.amount || 0);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('El abono debe ser mayor a 0.');
      return;
    }
    if (saldo > 0 && amount - saldo > 0.01) {
      setError('El abono excede el saldo.');
      return;
    }
    setError('');
    onSave({
      date: form.date,
      amount,
      method: form.method || '',
      comment: form.comment || '',
    });
  };

  return (
    <Stack spacing={2} sx={{ p: 1 }}>
      <Typography variant="caption" color="text.secondary">
        Saldo actual: {formatCurrency(saldo)}
      </Typography>
      <TextField
        size="small"
        label="Fecha"
        type="date"
        value={form.date}
        onChange={(e) => handleChange('date', e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        size="small"
        label="Monto"
        type="number"
        inputProps={{ step: '0.01' }}
        value={form.amount}
        onChange={(e) => handleChange('amount', e.target.value)}
      />
      <TextField
        size="small"
        label="Metodo (opcional)"
        value={form.method}
        onChange={(e) => handleChange('method', e.target.value)}
      />
      <TextField
        size="small"
        label="Comentario"
        value={form.comment}
        onChange={(e) => handleChange('comment', e.target.value)}
        multiline
        rows={2}
      />
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
      <Box sx={{ textAlign: 'right' }}>
        <Button variant="contained" onClick={handleSubmit}>
          Abonar
        </Button>
      </Box>
    </Stack>
  );
}

function BillPaymentForm({ onSave, bill }) {
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const remaining = Number(bill?.saldo_amount || 0);
  const validAmount = Number(amount || 0);
  const isValid =
    Number.isFinite(validAmount)
    && validAmount > 0
    && validAmount <= remaining + 0.0001;
  const isCancelAction = isValid && Math.abs(validAmount - remaining) <= 0.01;

  useEffect(() => {
    const next = remaining > 0 ? Number(remaining).toFixed(2) : '';
    setAmount(next);
    setError('');
  }, [remaining, bill?.id]);

  const handleSubmit = () => {
    const val = Number(amount || 0);
    if (!Number.isFinite(val) || val <= 0) {
      setError('El monto debe ser mayor a 0.');
      return;
    }
    if (val > remaining + 0.0001) {
      setError('El monto no puede exceder el saldo pendiente.');
      return;
    }
    setError('');
    onSave({ amount: Number(val.toFixed(2)) });
  };

  return (
    <Stack spacing={2} sx={{ p: 1 }}>
      <Typography variant="caption" color="text.secondary">
        Saldo pendiente: {formatCurrency(remaining)}
      </Typography>
      <TextField
        size="small"
        label="Monto a aplicar"
        type="number"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        inputProps={{ min: 0, max: remaining, step: '0.01' }}
      />
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
      <Box sx={{ textAlign: 'right' }}>
        <Button variant="contained" onClick={handleSubmit} disabled={!isValid}>
          {isCancelAction ? 'Cancelar bill' : 'Pagar bill'}
        </Button>
      </Box>
    </Stack>
  );
}

function DebtDetail({ detail }) {
  const payments = Array.isArray(detail?.payments) ? detail.payments : [];

  return (
    <Stack spacing={1.5} sx={{ p: 1 }}>
      <Typography variant="subtitle2">
        Deuda {detail?.number ? `#${detail.number}` : `#${detail?.id || ''}`}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Proveedor: {detail?.supplier_name || detail?.supplier_legal_name || '—'}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Total: {formatCurrency(detail?.total_amount)} | Abonado: {formatCurrency(detail?.paid_amount)} | Saldo: {formatCurrency(detail?.saldo_amount)}
      </Typography>

      {payments.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Sin abonos registrados.
        </Typography>
      ) : (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          {payments.map((p) => (
            <Box
              key={p.id}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                px: 1.5,
                py: 0.75,
                borderBottom: '1px solid',
                borderBottomColor: 'divider',
              }}
            >
              <Typography variant="caption">{formatDate(p.date)}</Typography>
              <Typography variant="caption">{p.method || '-'}</Typography>
              <Typography variant="caption">{formatCurrency(p.amount)}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Stack>
  );
}

export default function DebtList() {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ search: '', status: 'ALL', supplierId: 'ALL' });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openPayment, setOpenPayment] = useState(false);
  const [openBillPayment, setOpenBillPayment] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [detail, setDetail] = useState(null);
  const [listSource, setListSource] = useState('debts');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    dispatch(addTab({ label: 'Deudas', path: '/debts/' }));
  }, [dispatch]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search || ''), 450);
    return () => clearTimeout(t);
  }, [filters.search]);

  const selectedRow = useMemo(() => rows.find((r) => r.id === selectedIds[0]), [rows, selectedIds]);
  const canAddPayment = Boolean(selectedRow && selectedRow.paymentStatus && selectedRow.source !== 'bill');
  const canSettleBill = Boolean(selectedRow && selectedRow.source === 'bill' && Number(selectedRow.saldo_amount || 0) > 0.0001);

  const loadSuppliers = useCallback(async () => {
    try {
      const res = await API.get('suppliers/minimal/', { params: { active: true } });
      setSuppliers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Error cargando proveedores de deuda', e);
      setSuppliers([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      if (filters.status && filters.status !== 'ALL') {
        params.status = filters.status === 'PAID' ? 'paid' : 'pending';
      }
      if (filters.supplierId && filters.supplierId !== 'ALL') {
        params.supplier_id = filters.supplierId;
      }
      const res = await SettlementsAPI.listDebts(params);
      const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      console.log('Cargando deudas', data);
      const mappedDebts = data.map((d) => ({ id: d.id ?? d.pk, source: 'debt', ...d }));
      if (mappedDebts.length > 0) {
        setRows(mappedDebts);
        setListSource('debts');
        return;
      }

      const resBills = await SettlementsAPI.bills(params);
      const billsData = Array.isArray(resBills.data) ? resBills.data : (resBills.data?.results || []);
      console.log('Cargando bills', billsData);
      const mappedBills = billsData.map((b) => {
        const total = Number(b.total_amount ?? b.amount ?? 0);
        const saldoRaw = Number(b.saldo_amount ?? b.saldo_pendiente ?? b.remaining_amount ?? 0);
        const saldo = Number.isFinite(saldoRaw) && saldoRaw > 0 ? saldoRaw : 0;
        const paidRaw = Number(b.paid_amount ?? (total - saldo));
        const paid = Number.isFinite(paidRaw) && paidRaw > 0 ? Math.min(total, paidRaw) : Math.max(0, total - saldo);
        const pending = saldo > 0.0001;
        return {
          id: b.id ?? b.pk,
          source: 'bill',
          number: b.number || (b.id ? `BILL-${b.id}` : ''),
          supplier_name: b.supplier_name || '',
          supplier_legal_name: b.supplier_legal_name || '',
          description: b.reason || b.comment || '',
          date: b.date || b.dateTime || '',
          total_amount: total,
          paid_amount: paid,
          saldo_amount: saldo,
          paymentStatus: pending,
        };
      });
      setRows(mappedBills);
      console.log('Formateado: ', mappedBills)
      setListSource('bills');
    } catch (e) {
      console.error('Error cargando deudas', e);
      const status = e?.response?.status;
      const message = status === 403
        ? 'No autorizado para ver deudas'
        : status === 404
          ? 'No se encontraron deudas'
          : 'No se pudo cargar deudas';
      setSnack({ open: true, message, severity: 'error' });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filters.status, filters.supplierId]);

  useEffect(() => { loadSuppliers(); }, [loadSuppliers]);
  useEffect(() => { load(); }, [load]);

  const handleCreateDebt = async (payload) => {
    try {
      await SettlementsAPI.createDebt(payload);
      setOpenCreate(false);
      setSnack({ open: true, message: 'Deuda creada', severity: 'success' });
      load();
    } catch {
      setSnack({ open: true, message: 'No se pudo crear la deuda', severity: 'error' });
    }
  };

  const handleAddPayment = async (payload) => {
    if (!selectedRow) return;
    try {
      await SettlementsAPI.addDebtPayment(selectedRow.id, payload);
      setOpenPayment(false);
      setSnack({ open: true, message: 'Abono registrado', severity: 'success' });
      load();
    } catch {
      setSnack({ open: true, message: 'No se pudo registrar el abono', severity: 'error' });
    }
  };

  const handlePayBill = async (payload) => {
    if (!selectedRow || selectedRow.source !== 'bill') return;
    try {
      await SettlementsAPI.payBill(selectedRow.id, payload);
      setOpenBillPayment(false);
      setSnack({ open: true, message: 'Pago de bill aplicado', severity: 'success' });
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'No se pudo aplicar el pago al bill';
      setSnack({ open: true, message: detail, severity: 'error' });
    }
  };

  const openDebtDetail = async (row) => {
    if (!row?.id) return;
    if (row.source === 'bill') {
      setDetail({ ...row, payments: [] });
      setOpenDetail(true);
      return;
    }
    try {
      const res = await SettlementsAPI.debtDetail(row.id);
      setDetail(res.data || null);
      setOpenDetail(true);
    } catch (e) {
      console.error('Error cargando detalle de deuda', e);
      const status = e?.response?.status;
      const message = status === 403
        ? 'No autorizado para ver el detalle'
        : status === 404
          ? 'Detalle no encontrado'
          : 'No se pudo cargar el detalle';
      setDetail(null);
      setSnack({ open: true, message, severity: 'error' });
    }
  };

  const columns = useMemo(() => ([
    { field: 'id', headerName: 'ID', width: 80 },
    {
      field: 'number',
      headerName: 'Numero',
      width: 140,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {row.number || row.id}
        </Typography>
      ),
    },
    {
      field: 'supplier_legal_name',
      headerName: 'Proveedor',
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => {
        return params || '';
      },
    },
    {
      field: 'date',
      headerName: 'Fecha',
      width: 120,
      valueGetter: (params) => {
        return formatDate(params);
      }
    },
    {
      field: 'total_amount',
      headerName: 'Total',
      width: 130,
      renderCell: ({ value }) => <Typography variant="body2">{formatCurrency(value)}</Typography>,
    },
    {
      field: 'paid_amount',
      headerName: 'Abonado',
      width: 130,
      renderCell: ({ value }) => <Typography variant="body2">{formatCurrency(value)}</Typography>,
    },
    {
      field: 'saldo_amount',
      headerName: 'Saldo',
      width: 130,
      renderCell: ({ value }) => <Typography variant="body2">{formatCurrency(value)}</Typography>,
    },
    {
      field: 'paymentStatus',
      headerName: 'Estado',
      width: 130,
      renderCell: ({ value, row }) => (
        <Chip
          size="small"
          label={value ? 'Pendiente' : (row?.source === 'bill' ? 'Cancelado' : 'Pagado')}
          color={value ? 'warning' : 'success'}
          variant="outlined"
        />
      ),
    },
  ]), []);

  const clearFilters = () => setFilters({ search: '', status: 'ALL', supplierId: 'ALL' });

  const supplierLabelMap = useMemo(
    () => suppliers.reduce((acc, s) => {
      acc[String(s.id)] = s?.companyName || s?.legalName || `Proveedor ${s.id}`;
      return acc;
    }, {}),
    [suppliers]
  );

  const selectedSupplierLabel = filters.supplierId === 'ALL'
    ? 'Todos'
    : (supplierLabelMap[String(filters.supplierId)] || 'Todos');

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" sx={{ mb: 2, gap: 2 }}>
        <MainText title="Deudas" subtitle="Control de deudas por proveedor." icon={AccountBalanceWallet} width="auto" />
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <Button variant="contained" size="small" startIcon={<Add />} onClick={() => setOpenCreate(true)}>
            Crear deuda
          </Button>
          <Tooltip title={canAddPayment ? '' : 'Selecciona una deuda pendiente'}>
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PendingActions />}
                disabled={!canAddPayment}
                onClick={() => setOpenPayment(true)}
              >
                Abonar
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={canSettleBill ? '' : 'Selecciona un bill pendiente'}>
            <span>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                startIcon={<Paid />}
                disabled={!canSettleBill}
                onClick={() => setOpenBillPayment(true)}
              >
                Pagar/Cancelar bill
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" gap={1.5} sx={{ mb: 1 }}>
        <TextField
          size="small"
          placeholder="Buscar numero, proveedor o descripcion..."
          value={filters.search}
          onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
          InputProps={{ startAdornment: (<InputAdornment position="start"><Search fontSize="small" /></InputAdornment>) }}
          sx={{ minWidth: { xs: '100%', md: 300 } }}
        />
        <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 240 } }}>
          <InputLabel id="debt-supplier-filter-label">Proveedor</InputLabel>
          <Select
            labelId="debt-supplier-filter-label"
            label="Proveedor"
            value={filters.supplierId}
            onChange={(e) => setFilters((p) => ({ ...p, supplierId: e.target.value }))}
            renderValue={(value) => (value === 'ALL' ? 'Todos' : (supplierLabelMap[String(value)] || 'Todos'))}
          >
            <MenuItem value="ALL">Todos</MenuItem>
            {(suppliers || []).map((s) => (
              <MenuItem key={s.id} value={String(s.id)}>
                {s?.companyName || s?.legalName || `Proveedor ${s.id}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel id="debt-status-filter-label">Estado</InputLabel>
          <Select
            labelId="debt-status-filter-label"
            label="Estado"
            value={filters.status}
            onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
            renderValue={(value) => {
              if (value === 'PENDING') return 'Pendientes';
              if (value === 'PAID') return 'Canceladas';
              return 'Todos';
            }}
          >
            <MenuItem value="ALL">Todos</MenuItem>
            <MenuItem value="PENDING">Pendientes</MenuItem>
            <MenuItem value="PAID">Canceladas</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ ml: { xs: 0, md: 'auto' }, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Refrescar"><IconButton size="small" onClick={load}><Refresh fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Limpiar filtros"><IconButton size="small" onClick={clearFilters}><Close fontSize="small" /></IconButton></Tooltip>
        </Box>
      </Stack>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        {filters.search && <Chip label={`Busqueda: "${filters.search}"`} onDelete={() => setFilters((p) => ({ ...p, search: '' }))} />}
        {filters.status !== 'ALL' && (
          <Chip
            label={`Estado: ${filters.status === 'PAID' ? 'Canceladas' : 'Pendientes'}`}
            onDelete={() => setFilters((p) => ({ ...p, status: 'ALL' }))}
          />
        )}
        {filters.supplierId !== 'ALL' ? (
          <Chip
            label={`Proveedor: ${selectedSupplierLabel}`}
            onDelete={() => setFilters((p) => ({ ...p, supplierId: 'ALL' }))}
          />
        ) : null}
        {listSource === 'bills' && (
          <Chip label="Mostrando saldos a favor" color="warning" variant="outlined" />
        )}
      </Box>

      <Box sx={{ height: 460, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableColumnFilter
          disableColumnMenu
          pageSizeOptions={[10, 20, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
          rowHeight={52}
          columnHeaderHeight={50}
          onRowSelectionModelChange={(m) => setSelectedIds(m)}
          rowSelectionModel={selectedIds}
          onRowDoubleClick={(params) => openDebtDetail(params.row)}
          sx={{
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#10173c', color: '#fff' },
            '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:hover': {
              backgroundColor: '#10173c !important',
            },
            '& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator': { color: '#ffffff !important' },
          }}
        />
      </Box>

      <CustomModal
        open={openCreate}
        setOpen={setOpenCreate}
        handleClose={() => setOpenCreate(false)}
        title="Crear deuda"
        icon={Add}
        width="35%"
        component={DebtCreateForm}
        componentProps={{ onSave: handleCreateDebt, suppliers }}
      />
      <CustomModal
        open={openPayment}
        setOpen={setOpenPayment}
        handleClose={() => setOpenPayment(false)}
        title="Registrar abono"
        icon={PendingActions}
        width="35%"
        component={DebtPaymentForm}
        componentProps={{ onSave: handleAddPayment, debt: selectedRow }}
      />
      <CustomModal
        open={openBillPayment}
        setOpen={setOpenBillPayment}
        handleClose={() => setOpenBillPayment(false)}
        title="Pagar/Cancelar bill"
        icon={Paid}
        width="35%"
        component={BillPaymentForm}
        componentProps={{ onSave: handlePayBill, bill: selectedRow }}
      />
      <CustomModal
        open={openDetail}
        setOpen={setOpenDetail}
        handleClose={() => setOpenDetail(false)}
        title="Detalle de deuda"
        icon={AccountBalanceWallet}
        width="40%"
        component={DebtDetail}
        componentProps={{ detail }}
      />

      <CustomSnackBar
        open={snack.open}
        setOpen={(val) => setSnack((p) => ({ ...p, open: val }))}
        message={snack.message}
        severity={snack.severity}
      />
    </Box>
  );
}
